import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { LuArrowLeftToLine, LuArrowRightToLine, LuBrush, LuChevronLeft, LuChevronRight, LuGripVertical, LuMoreHorizontal, LuPencil, LuPlus, LuTrash2 } from 'react-icons/lu';
import { BsWhatsapp } from 'react-icons/bs';
import { resolveInternationalPhone } from '../../../utils/phone';
import { avatarColor, initials } from '../BotsContent/Others/BotListKit';

export const STAGE_COLORS = ['#2563eb', '#0891b2', '#7c3aed', '#d97706', '#0d9488', '#db2777', '#16a34a', '#64748b'];

const TEMPERATURES = {
    caliente: { color: '#dc2626', label: 'Caliente' },
    tibio: { color: '#d97706', label: 'Tibio' },
    frio: { color: '#0891b2', label: 'Frío' },
};

const temperatureOf = (item) => {
    const interest = item.dominantBuyingInterest || {};
    const key = String(interest.temperature || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    if (TEMPERATURES[key]) return TEMPERATURES[key];
    const percentage = Number(interest.percentage);
    if (!interest.percentage || Number.isNaN(percentage)) return { color: '#C3C3C3', label: 'Sin datos' };
    if (percentage <= 49) return TEMPERATURES.frio;
    if (percentage <= 79) return TEMPERATURES.tibio;
    return TEMPERATURES.caliente;
};

const StageMenu = ({ stage, isFirst, isLast, onAction }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const hasProspects = (stage.prospects || []).filter(Boolean).length > 0;

    useEffect(() => {
        if (!open) return undefined;
        const onMouseDown = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const run = (action, value) => {
        setOpen(false);
        onAction(stage._id, action, value);
    };

    const item = (label, Icon, action, { danger, disabled } = {}) => (
        <button type="button" role="menuitem" className={`su-pop-row${danger ? ' danger' : ''}`} disabled={disabled} onClick={() => run(action)}>
            <Icon className="su-ic" size={15} />
            {label}
        </button>
    );

    return (
        <div ref={ref} className="kc-menu">
            <button type="button" className={`kc-ib${open ? ' on' : ''}`} aria-label="Acciones de la etapa" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                <LuMoreHorizontal className="su-ic" size={17} />
            </button>
            {open && (
                <div className="su-pop" role="menu">
                    {item('Renombrar etapa', LuPencil, 'startRename')}
                    {item('Mover a la izquierda', LuChevronLeft, 'moveLeft', { disabled: isFirst })}
                    {item('Mover a la derecha', LuChevronRight, 'moveRight', { disabled: isLast })}
                    <div className="su-pop-sep" />
                    {item('Etapa a la izquierda', LuArrowLeftToLine, 'addLeft')}
                    {item('Etapa a la derecha', LuArrowRightToLine, 'addRight')}
                    <div className="su-pop-sep" />
                    <div className="kcol-colors" role="group" aria-label="Color de la etapa">
                        {STAGE_COLORS.map((color) => (
                            <button
                                key={color}
                                type="button"
                                aria-label={`Color ${color}`}
                                aria-pressed={stage.color === color}
                                className={`kcol-color${stage.color === color ? ' on' : ''}`}
                                style={{ backgroundColor: color }}
                                onClick={() => run('color', color)}
                            />
                        ))}
                    </div>
                    <div className="su-pop-sep" />
                    {hasProspects && item('Vaciar etapa', LuBrush, 'empty')}
                    {item('Eliminar etapa', LuTrash2, 'delete', { danger: true })}
                </div>
            )}
        </div>
    );
};

const StageName = ({ stage, editing, onStart, onSave, onCancel }) => {
    const [value, setValue] = useState(stage.name || '');

    useEffect(() => {
        if (editing) setValue(stage.name || '');
    }, [editing, stage.name]);

    if (!editing) {
        return (
            <span className="kcol-name" title="Doble clic para renombrar" onDoubleClick={onStart}>
                {stage.name}
            </span>
        );
    }

    const save = () => {
        const name = value.trim();
        if (name && name !== stage.name) onSave(name);
        else onCancel();
    };

    return (
        <input
            className="kcol-name-edit"
            autoFocus
            aria-label="Nombre de la etapa"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onFocus={(event) => event.target.select()}
            onBlur={save}
            onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
                if (event.key === 'Escape') onCancel();
            }}
        />
    );
};

const Process = ({
    isColumnDragDisabled,
    listStages,
    handleContactInfo,
    setNameConv,
    setActiveId,
    fetch_messages,
    setInfoContact,
    setShowContacts,
    setShowContactInfo,
    setStageSelected,
    onDragStart,
    onDragEnd,
    onAddStage,
    onStageAction,
}) => {
    const [renamingId, setRenamingId] = useState(null);
    const stages = listStages.filter((stage) => !stage.new);

    const handleAction = (stageId, action, value) => {
        if (action === 'startRename') {
            setRenamingId(stageId);
            return;
        }
        onStageAction(stageId, action, value);
    };

    return (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable-columns" direction="horizontal" isDropDisabled={isColumnDragDisabled}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="kboard">
                        {stages.map((stage, index) => {
                            const color = stage.color || STAGE_COLORS[index % STAGE_COLORS.length];
                            const prospects = (stage.prospects || []).filter((item) => item != null);
                            return (
                                <Draggable key={stage._id} draggableId={String(stage._id)} index={index} isDragDisabled={renamingId === stage._id || Boolean(stage.pending)}>
                                    {(columnProvided, columnSnapshot) => (
                                        <div ref={columnProvided.innerRef} {...columnProvided.draggableProps} className={`kcol${columnSnapshot.isDragging ? ' col-dragging' : ''}${stage.pending ? ' pending' : ''}`} style={{ ...columnProvided.draggableProps.style }}>
                                            <Droppable key={stage._id} droppableId={stage._id.toString()} direction="vertical">
                                                {(provided, snapshot) => (
                                                    <div ref={provided.innerRef} {...provided.droppableProps} className={`kcol-in${snapshot.isDraggingOver ? ' over' : ''}`}>
                                                        <div className="kcol-head" {...columnProvided.dragHandleProps}>
                                                            <span className="kcol-grip" title="Arrastra para reordenar">
                                                                <LuGripVertical className="su-ic" size={15} />
                                                            </span>
                                                            <span className="kcol-bar" style={{ backgroundColor: color }} />
                                                            <StageName
                                                                stage={stage}
                                                                editing={renamingId === stage._id}
                                                                onStart={() => !stage.pending && setRenamingId(stage._id)}
                                                                onCancel={() => setRenamingId(null)}
                                                                onSave={(name) => {
                                                                    setRenamingId(null);
                                                                    onStageAction(stage._id, 'rename', name);
                                                                }}
                                                            />
                                                            <span className="kcol-count">{prospects.length}</span>
                                                            {!stage.pending && <StageMenu stage={{ ...stage, color }} isFirst={index === 0} isLast={index === stages.length - 1} onAction={handleAction} />}
                                                        </div>

                                                        <div className="kcol-body">
                                                            {prospects.map((item, itemIndex) => {
                                                                const temperature = temperatureOf(item);
                                                                const tags = Array.isArray(item.tags) ? item.tags.filter((tag) => tag != null) : [];
                                                                return (
                                                                    <Draggable key={item._id} draggableId={item._id.toString()} index={itemIndex}>
                                                                        {(cardProvided, cardSnapshot) => (
                                                                            <div
                                                                                className={`pc${cardSnapshot.isDragging ? ' dragging' : ''}`}
                                                                                ref={cardProvided.innerRef}
                                                                                {...cardProvided.draggableProps}
                                                                                {...cardProvided.dragHandleProps}
                                                                                style={{ ...cardProvided.draggableProps.style, zIndex: cardSnapshot.isDragging ? 9999 : 'auto' }}
                                                                                onClick={() => handleContactInfo(item, stage._id)}
                                                                            >
                                                                                <span className="pc-spine" style={{ backgroundColor: temperature.color }} data-tooltip-id="my-tooltip" data-tooltip-content={`Interés de compra: ${temperature.label}`} />
                                                                                <div className="pc-head">
                                                                                    <span className="pc-av-wrap">
                                                                                        <span className="pc-av" style={{ backgroundColor: avatarColor(item.name) }}>
                                                                                            {initials(item.name)}
                                                                                        </span>
                                                                                        {item.dominantFeeling?.emoji && (
                                                                                            <span className="pc-feel" data-tooltip-id="my-tooltip" data-tooltip-content={`${item.dominantFeeling.percentage}% ${item.dominantFeeling.description}`}>
                                                                                                {item.dominantFeeling.emoji}
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                    <div className="pc-id">
                                                                                        <div className="pc-name" title={item.name}>
                                                                                            {item.name}
                                                                                        </div>
                                                                                        <div className="pc-phone">+{resolveInternationalPhone(item.callingCode, item.phone)}</div>
                                                                                    </div>
                                                                                </div>
                                                                                {tags.length > 0 && (
                                                                                    <div className="pc-tags">
                                                                                        {tags.slice(0, 3).map((tag, t) => (
                                                                                            <span key={t} className="pc-tag" style={{ '--c': tag.color || '#8B8B8B' }}>
                                                                                                {tag.title}
                                                                                            </span>
                                                                                        ))}
                                                                                        {tags.length > 3 && (
                                                                                            <span className="pc-tagmore" data-tooltip-id="my-tooltip" data-tooltip-content={tags.slice(3).map((tag) => tag.title).join(', ')}>
                                                                                                +{tags.length - 3}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                <div className="pc-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="pc-act"
                                                                                        aria-label={item.isConv ? 'Enviar mensaje' : 'Crear conversación'}
                                                                                        data-tooltip-id="my-tooltip"
                                                                                        data-tooltip-content={item.isConv ? 'Enviar mensaje' : 'Crear conversación'}
                                                                                        onClick={(event) => {
                                                                                            event.stopPropagation();
                                                                                            setNameConv(item.name);
                                                                                            setActiveId(true);
                                                                                            fetch_messages(resolveInternationalPhone(item.callingCode, item.phone));
                                                                                            setInfoContact(item);
                                                                                        }}
                                                                                    >
                                                                                        <BsWhatsapp size={15} />
                                                                                        WhatsApp
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                );
                                                            })}
                                                            {prospects.length === 0 && <div className="kcol-empty">{snapshot.isDraggingOver ? 'Soltar aquí' : 'Sin prospectos'}</div>}
                                                            {provided.placeholder}
                                                            <button
                                                                type="button"
                                                                className="kcol-add"
                                                                onClick={() => {
                                                                    setShowContacts(true);
                                                                    setShowContactInfo(false);
                                                                    setStageSelected({ ...stage, prospects: [...(stage.prospects || [])] });
                                                                }}
                                                            >
                                                                <LuPlus className="su-ic" size={16} />
                                                                Agregar contactos
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    )}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                        <button type="button" className="kcol-addstage" onClick={onAddStage}>
                            <LuPlus className="su-ic" size={18} />
                            Agregar etapa
                        </button>
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default Process;
